import { Input } from "@/components/podkit/forms/Input";
import { Button } from "@/components/flexkit/Button";
import { useCallback, useId, useState } from "react";
import { InputField } from "@/components/podkit/forms/InputField";
import { formatError } from "@/utils/errors";
import { IconClose } from "@/assets/icons/geist/IconClose";

export type TagsInputProps = {
    tags: string[];
    label?: string;
    placeHolder?: string;
    validateInputFn?: (value: string) => boolean;
    onAddValue?: (value: string) => Promise<void> | void;
    onRemoveTag?: (tag: string) => Promise<void> | void;
};

export const TagsInput: React.FC<TagsInputProps> = ({
    label,
    tags,
    placeHolder,
    validateInputFn,
    onAddValue,
    onRemoveTag,
}) => {
    const [inputString, setInputString] = useState<string>("");
    const [errorText, setErrorText] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const inputId = useId();

    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setErrorText("");
            setSubmitting(true);

            if (validateInputFn) {
                const isValid = validateInputFn(inputString);
                if (!isValid) {
                    setErrorText("Invalid value");
                    setSubmitting(false);
                    return;
                }
            }

            if (onAddValue) {
                try {
                    await onAddValue(inputString);
                    setInputString("");
                } catch (error) {
                    setErrorText(formatError(error));
                }
            }

            setSubmitting(false);
        },
        [inputString, onAddValue, validateInputFn],
    );

    const handleRemove = useCallback(
        async (tag: string) => {
            setErrorText("");
            if (onRemoveTag) {
                try {
                    await onRemoveTag(tag);
                } catch {
                    // ignore
                }
            }
        },
        [onRemoveTag],
    );

    return (
        <>
            <div className="flex flex-col gap-2">
                <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
                    <InputField label={label} disabled={false} error={errorText} id={inputId}>
                        <div className="flex flex-row flex-wrap gap-2">
                            {tags.map((tag, i) => (
                                <div
                                    key={`tag-${i}-${tag}`}
                                    className="flex w-fit flex-row items-center gap-1 rounded-md border border-border-base bg-surface-positive px-1 py-0.5 text-sm text-content-tertiary"
                                >
                                    <span className="px-2">{tag}</span>
                                    <Button
                                        onClick={() => handleRemove(tag)}
                                        type="button"
                                        variant="ghost"
                                        className="border-none"
                                        size="xs"
                                        LeadingIcon={IconClose}
                                        data-tracking-id={`remove-${tag}-tags-input`}
                                    />
                                </div>
                            ))}
                        </div>
                        <Input
                            placeholder={placeHolder}
                            value={inputString}
                            id={inputId}
                            onChange={(e) => {
                                setErrorText("");
                                setInputString(e.target.value.toLocaleLowerCase());
                            }}
                        />
                    </InputField>
                    <Button
                        type="submit"
                        variant="secondary"
                        className="w-fit"
                        disabled={!inputString}
                        loading={submitting}
                        data-tracking-id="add-tags-input"
                    >
                        Add
                    </Button>
                </form>
            </div>
        </>
    );
};
TagsInput.displayName = "TagsInput";
